import { useState } from 'react'
import { Modal, Button, Group, Stack, Select, Textarea, Text } from '@mantine/core'
import { useMutation } from '@apollo/client/react'
import ReportTrackProblem from '../graphql/queries/ReportTrackProblem'

const PROBLEM_TYPE_OPTIONS = [
    { value: 'BAD_METADATA', label: 'Incorrect metadata (title, artist, or album)' },
    { value: 'BAD_TAGS', label: 'Incorrect tags' },
    { value: 'INVALID_YOUTUBE_ID', label: 'Incorrect or unplayable YouTube video' },
    { value: 'OTHER', label: 'Other' },
]

const DESCRIPTION_PLACEHOLDERS = {
    BAD_METADATA: 'Which fields are wrong and what should they be? (e.g. "Artist should be \"The Beatles\", Album should be \"Abbey Road\"")',
    BAD_TAGS:     'Which tags should be added or removed? (e.g. "Remove #rock, add #jazz")',
    INVALID_YOUTUBE_ID: 'What is the correct YouTube video ID or URL?',
    OTHER:        'Describe the issue (optional)',
}

export default function ReportTrackProblemModal({ opened, onClose, trackId, trackTitle }) {
    const [problemType, setProblemType] = useState(null)
    const [description, setDescription] = useState('')
    const [submitted, setSubmitted] = useState(false)

    const [reportTrackProblem, { loading }] = useMutation(ReportTrackProblem)

    const handleSubmit = async () => {
        if (!problemType) return
        try {
            await reportTrackProblem({
                variables: {
                    trackId,
                    problemType,
                    description: description.trim() || undefined,
                }
            })
            setSubmitted(true)
        } catch (e) {
            console.error('Failed to submit track problem report', e)
        }
    }

    const handleClose = () => {
        setProblemType(null)
        setDescription('')
        setSubmitted(false)
        onClose()
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Report a Problem"
            centered
            styles={{
                title: {
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }
            }}
        >
            {submitted ? (
                <Stack gap="md" style={{ marginTop: '0.5em' }}>
                    <Text>Thanks for the report! We'll look into it.</Text>
                    <Group justify="flex-end" mt="xs">
                        <Button onClick={handleClose}>Close</Button>
                    </Group>
                </Stack>
            ) : (
                <Stack gap="md" style={{ marginTop: '0.5em' }}>
                    {trackTitle && (
                        <Text size="md" fw={600}>{trackTitle}</Text>
                    )}
                    <Select
                        label="What's the problem?"
                        placeholder="Choose a problem type"
                        data={PROBLEM_TYPE_OPTIONS}
                        value={problemType}
                        onChange={setProblemType}
                        required
                    />
                    <Textarea
                        label="Additional details"
                        placeholder={DESCRIPTION_PLACEHOLDERS[problemType] ?? 'Describe the issue (optional)'}
                        value={description}
                        onChange={(e) => setDescription(e.currentTarget.value)}
                        minRows={3}
                        autosize
                    />
                    <Group justify="flex-end" mt="xs">
                        <Button variant="subtle" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} loading={loading} disabled={!problemType}>
                            Submit Report
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    )
}




